import {
  Component,
  createSignal,
  createMemo,
  onMount,
  Show,
  For,
} from 'solid-js';
import { createForm } from '@tanstack/solid-form';
import { ITagItem } from '@tcweb/connect-ui';
import { translate } from '@fulcrum/lib';
import {
  useApiServices,
  ExportFormat,
  IExportSettings,
  ICreateExportJobPayload,
  IExportJobDetail,
} from '@trimble/tc-issue-management-providers';
import { getLogger } from '@trimble/logging';
import { IssueManagementWidgetMode } from '../../types/issue.types';
import {
  defineSolidCustomCheckbox,
  defineSolidCustomInput,
  defineSolidPopup,
} from '@tcweb/connect-ui';
import './IssueExport.css';
// Types for export functionality
export type ExportOptionConfig =
  | 'viewpoints'
  | 'comments'
  | 'documents'
  | 'image';

export interface IFormData {
  projectId: string;
  title: string;
  name: string;
  description: string;
  priority: string;
  dueDate: Date | string;
  type: string;
  selectedConfig: ExportOptionConfig[];
  formatConfig: ExportFormat;
}

export interface ITFilterData {
  types: string[];
  status: string[];
  priority: string[];
  tags: string[];
}

export interface IExportDialogueMode {
  mode: 'FULL' | 'SELECTED';
}

export interface IssueExportProps {
  show: boolean;
  onClose(): void;
  projectId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- filteredIssueList structure varies
  filteredIssueList?: any[];
  selectedRows?: number[];
  exportMode?: 'FULL' | 'SELECTED';
  searchItems?: ITagItem[];
  setSearchItems?(items: ITagItem[]): void;
  isAssignedToMe?: boolean;
  toggleAssignedToMe?(): void;
  filterData?: ITFilterData;
  setFilterData?(data: ITFilterData): void;
  onFilter?(filterData: ITFilterData): void;
  exportConfig?: ExportOptionConfig[];
  exportFormatConfig?: ExportFormat;
  existingQuery?: string;
  currentUserEmail?: string;
  embedIn3D?: boolean;
  setWidgetMode?(mode: IssueManagementWidgetMode): void;
  clearExports?(): void;
  addExports?(jobs: IExportJobDetail[]): void;
}

const logger = getLogger();

export const IssueExport: Component<IssueExportProps> = (props) => {
  // Initialize web components
  onMount(() => {
    defineSolidCustomCheckbox();
    defineSolidCustomInput();
    defineSolidPopup();

    if (props.setWidgetMode) {
      props.setWidgetMode(IssueManagementWidgetMode.IssueExport);
    }

    if (props.clearExports) {
      props.clearExports();
    }
  });

  // Get API services
  const apiServices = useApiServices();

  // Signals for component state
  const [isUpdating, setIsUpdating] = createSignal(false);
  const [searchQuery, setSearchQuery] = createSignal('');

  // Configuration options for export
  const configOptions = [
    {
      name: 'viewpoints' as ExportOptionConfig,
      label: translate('TC.Topic.Viewpoints'),
    },
    {
      name: 'comments' as ExportOptionConfig,
      label: translate('comments'),
    },
    {
      name: 'documents' as ExportOptionConfig,
      label: translate('TC.Topic.RefDoc'),
    },
  ];

  const exportFormatOptions = [
    {
      name: 'BCF2_1' as ExportFormat,
      label: 'BCF 2.1 (.bcf)',
    },
    {
      name: 'BCF3_0' as ExportFormat,
      label: 'BCF 3.0 (.bcf)',
    },
    {
      name: 'PDF' as ExportFormat,
      label: translate('TC.Topic.ExportPdfFormat'),
    },
  ];

  // Form setup with TanStack Form
  const exportForm = createForm(() => ({
    defaultValues: {
      projectId: props.projectId || '',
      title: '',
      name: '',
      description: '',
      priority: '',
      dueDate: '',
      type: '',
      selectedConfig: [],
      formatConfig: 'BCF2_1',
    } as IFormData,
    onSubmit: async ({ value }) => {
      await handleExportSubmit(value);
    },
  }));

  // Predefined search values for tag editor
  const preDefinedValues = createMemo(() => [
    {
      isValid: true,
      label: `All: ${searchQuery()}`,
      value: `All:${searchQuery()}`,
    },
    {
      isValid: true,
      label: `Assignee: ${searchQuery()}`,
      value: `assignees:${searchQuery()}`,
    },
    {
      isValid: true,
      label: `Creator: ${searchQuery()}`,
      value: `creation_author_uuid:${searchQuery()}`,
    },
    {
      isValid: true,
      label: `Description: ${searchQuery()}`,
      value: `description:${searchQuery()}`,
    },
    {
      isValid: true,
      label: `Priority: ${searchQuery()}`,
      value: `priority:${searchQuery()}`,
    },
    {
      isValid: true,
      label: `Status: ${searchQuery()}`,
      value: `topic_status:${searchQuery()}`,
    },
    {
      isValid: true,
      label: `Tags: ${searchQuery()}`,
      value: `labels:${searchQuery()}`,
    },
    {
      isValid: true,
      label: `Title: ${searchQuery()}`,
      value: `title:${searchQuery()}`,
    },
    {
      isValid: true,
      label: `Type: ${searchQuery()}`,
      value: `topic_type:${searchQuery()}`,
    },
  ]);

  // Export submission handler
  const handleExportSubmit = async (values: IFormData): Promise<void> => {
    if (!apiServices) {
      logger.error('[IssueExport] API services not available');
      return;
    }

    setIsUpdating(true);

    try {
      // Build export settings
      const settings: IExportSettings = {
        viewpoints: values.selectedConfig.includes('viewpoints'),
        comments: values.selectedConfig.includes('comments'),
        refdocs: values.selectedConfig.includes('documents'),
        originalImages: values.selectedConfig.includes('image'),
      };

      // Build export payload
      const payload: ICreateExportJobPayload = {
        name: values.name || `Export ${new Date().toLocaleString()}`,
        type: 'EXPORT',
        settings,
        package: {
          format: values.formatConfig,
          archived: false,
        },
        filter: buildExportFilter(),
      };

      // Create export job
      const createResponse = await apiServices.issueExportApi.createExportJob(
        props.projectId,
        payload
      );

      // Job was created — close the dialog immediately so the user can't
      // accidentally submit again while we fetch the full job details.
      props.onClose();

      // Fetch full job details (results.log, results.links) and register with
      // the store. This is best-effort: a failure here must not re-open the
      // dialog or block the user, since the job already exists server-side.
      if (createResponse?.jobId) {
        try {
          const jobDetail = await apiServices.issueExportApi.getExportJobDetail(
            props.projectId,
            createResponse.jobId
          );

          if (props.addExports && jobDetail) {
            props.addExports([jobDetail]);
          }
        } catch (detailError) {
          logger.error(
            '[IssueExport] Export job created but failed to fetch job details:',
            detailError
          );
        }
      }
    } catch (error) {
      logger.error('[IssueExport] Export failed:', error);
      // TODO: Show error message to user
    } finally {
      setIsUpdating(false);
    }
  };

  // Build export filter based on current filters and selection
  const buildExportFilter = (): Record<string, unknown> => {
    // Access props directly - this function is called during form submission, not during render
    const currentFilterData = props.filterData;
    const currentExportMode = props.exportMode;
    const currentSelectedRows = props.selectedRows;
    const currentFilteredIssueList = props.filteredIssueList;
    const currentExistingQuery = props.existingQuery;
    const currentSearchItems = props.searchItems;
    const currentIsAssignedToMe = props.isAssignedToMe;
    const currentUserEmail = props.currentUserEmail;

    if (
      currentExportMode === 'SELECTED' &&
      currentSelectedRows &&
      currentFilteredIssueList
    ) {
      // Export selected items
      const selectedIds = currentSelectedRows
        .map((row) => currentFilteredIssueList[row]?.id)
        .filter(Boolean);
      return {
        and: [{ in: [{ var: 'guid' }, selectedIds] }],
      };
    }

    if (currentExistingQuery) {
      // Parse existing query or wrap it appropriately
      // For now, return an empty filter if existing query exists
      return {};
    }

    // Build filter from current filter data
    const filters = [];

    if (currentFilterData?.types?.length) {
      filters.push({
        or: currentFilterData.types.map((type) => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention -- '==' is a filter operator name from API
          '==': [{ var: 'topic_type' }, type],
        })),
      });
    }

    if (currentFilterData?.status?.length) {
      filters.push({
        or: currentFilterData.status.map((status) => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention -- '==' is a filter operator name from API
          '==': [{ var: 'topic_status' }, status],
        })),
      });
    }

    if (currentFilterData?.priority?.length) {
      filters.push({
        or: currentFilterData.priority.map((priority) => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention -- '==' is a filter operator name from API
          '==': [{ var: 'priority' }, priority],
        })),
      });
    }

    if (currentSearchItems?.length) {
      const searchFilters = currentSearchItems.map((item) => {
        const [field, value] = item.value.split(':');
        return field === 'All'
          ? {
              or: [
                { in: [value.toLowerCase(), { tolower: [{ var: 'title' }] }] },
                {
                  in: [
                    value.toLowerCase(),
                    { tolower: [{ var: 'description' }] },
                  ],
                },
                {
                  in: [
                    value.toLowerCase(),
                    { tolower: [{ var: 'creation_author' }] },
                  ],
                },
              ],
            }
          : { in: [value.toLowerCase(), { tolower: [{ var: field }] }] };
      });
      filters.push({ and: searchFilters });
    }

    if (currentIsAssignedToMe && currentUserEmail) {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- '==' is a filter operator name from API
      filters.push({ '==': [{ var: 'assigned_to' }, currentUserEmail] });
    }

    const EMPTY_FILTER_VALUE = '0';
    return filters.length > 0
      ? { and: filters }
      : // eslint-disable-next-line @typescript-eslint/naming-convention -- '!!' is a filter operator name from API
        { '!!': [EMPTY_FILTER_VALUE] };
  };

  // Handle primary button click
  const handlePrimaryClick = (): void => {
    if (!isUpdating()) {
      exportForm.handleSubmit();
    }
  };

  // Handle tag editor changes
  const handleTagChange = (tags: ITagItem[]): void => {
    props.setSearchItems?.(tags);
  };

  // Handle search query changes
  const handleSearchQueryChange = (query: string): void => {
    setSearchQuery(query);
  };

  const popupContent = (
    <div>
      {/* Export Name Input */}
      <div class="sub-section">
        <div class="section-details">
          <exportForm.Field name="name">
            {(field) => (
              <>
                <tc-custom-input
                  name="name"
                  label={translate('name')}
                  placeholder={translate('TC.Topic.ExportNamePlaceholder')}
                  value={field().state.value}
                  ref={(el: any) => {
                    if (el) {
                      el.addEventListener('tc-custominput-input', (e: any) => {
                        field().handleChange(e.detail.value);
                      });
                    }
                  }}
                />
              </>
            )}
          </exportForm.Field>
        </div>
      </div>

      {/* Full Export Parameters */}
      <Show when={props.exportMode === 'FULL'}>
        <div class="pt-2">
          <h5>{translate('TC.Topic.ExportParameters')}</h5>
        </div>
        <hr class="header-separation" />
        <div>
          {/* Search/Filter Section */}
          {/* @ts-expect-error - Web component type not available */}
          <tc-tag-editor
            place-holder={translate('TC.Common.Search')}
            selected-tag-list={props.searchItems || []}
            allow-custom-tag={false}
            not-found-error-msg={translate('TCW.Common.NoSearchFound')}
            match-case={false}
            predefined-tag-list={preDefinedValues().filter(
              (o) =>
                !props.searchItems?.find(
                  (o2) => o.value.split(':')[0] === o2.value.split(':')[0]
                )
            )}
            enable-search-icon={true}
            tag-list-title={translate('TC.Common.SearchBy')}
            max-length={100}
            ref={(el: any) => {
              if (el) {
                el.addEventListener('tc-tag-change', (e: any) => {
                  handleTagChange(e.detail.tags);
                });
                el.addEventListener('tc-tag-search', (e: any) => {
                  handleSearchQueryChange(e.detail.query);
                });
              }
            }}
          />

          {/* Filter Controls Row */}
          <div class="row">
            <div class="col-12 col-sm-6">
              <div class="input-group">
                <label>
                  <span>{translate('TC.Common.Type')}</span>
                </label>
                {/* TODO: Add type picker component */}
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="input-group">
                <label>
                  <span>{translate('TC.Common.Priority')}</span>
                </label>
                {/* TODO: Add priority picker component */}
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-sm-6">
              <div class="input-group">
                <label>
                  <span>{translate('TC.Common.Status')}</span>
                </label>
                {/* TODO: Add status picker component */}
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="export-padding">
                {/* TODO: Add assigned to me toggle */}
              </div>
            </div>
          </div>
        </div>
      </Show>

      {/* Export Options */}
      <div class="pt-2">
        <h5>{translate('TC.Topic.ExportOptions')}</h5>
      </div>
      <hr class="header-separation" />

      {/* Export Format Selection */}
      <div class="row">
        <exportForm.Field name="formatConfig">
          {(field) => (
            <For each={exportFormatOptions}>
              {(option, index) => (
                <div class="col-12 col-sm-4 pr-1">
                  <div class="input-group mb-0">
                    <label>
                      {index() === 0 ? translate('TC.Topic.ExportAsTitle') : ''}
                    </label>{' '}
                    <div class="group-items">
                      <div class="icon radio">
                        <input
                          id={`formatConfig-${option.name}`}
                          class="custom-input"
                          type="radio"
                          name="formatConfig"
                          value={option.name}
                          checked={field().state.value === option.name}
                          onChange={(e) => {
                            if (e.target.checked) {
                              field().handleChange(option.name as any);
                            }
                          }}
                        />
                        <label for={`formatConfig-${option.name}`} />
                      </div>
                      <div>
                        <label for={`formatConfig-${option.name}`}>
                          {option.label}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </For>
          )}
        </exportForm.Field>
      </div>

      {/* Include Options */}
      <div class="input-group mb-0">
        <label>
          <span>{translate('TC.Common.Include')}</span>
        </label>
        <For each={configOptions}>
          {(config) => (
            <div class="checkradio-group mb-0">
              <div class="input-group checkradio mb-0 min-h-auto">
                <div class="group-items">
                  <div class="icon checkbox">
                    <exportForm.Field name="selectedConfig">
                      {(field) => (
                        <>
                          {/* @ts-expect-error - Web component type not available */}
                          <tc-custom-checkbox
                            name="selectedConfig"
                            value={config.name}
                            checked={field().state.value.includes(config.name)}
                            onChange={(e: any) => {
                              const currentValues = field().state.value;
                              const newValues = e.target.checked
                                ? [...currentValues, config.name]
                                : currentValues.filter(
                                    (v: ExportOptionConfig) => v !== config.name
                                  );
                              field().handleChange(newValues);
                            }}
                          />
                        </>
                      )}
                    </exportForm.Field>
                    <label />
                  </div>
                  <div>
                    <label for={config.name}>{config.label}</label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </For>
      </div>
    </div>
  );

  return (
    <div class="issue-export-container">
      {/* @ts-expect-error - Web component type not available */}
      <tc-popup
        show={props.show !== false}
        heading={translate('TC.Topic.ExportBcfLabel')}
        hide-footer={false}
        primary-btn-label={
          isUpdating()
            ? translate('TC.Topic.ExportingTopics')
            : translate('exportLabel')
        }
        primary-btn-class="primary"
        primary-btn-disabled={isUpdating()}
        secondary-btn-label={translate('TC.Common.Cancel')}
        secondary-btn-class="link-secondary"
        secondary-btn-disabled={isUpdating()}
        content={popupContent}
        ref={(el: any) => {
          if (el) {
            // Add event listeners for popup events
            el.addEventListener('popup-close', () => {
              props.onClose();
            });
            el.addEventListener('popup-secondary-action', () => {
              props.onClose();
            });
            el.addEventListener('popup-primary-action', () => {
              handlePrimaryClick();
            });
          }
        }}
      />
    </div>
  );
};

export default IssueExport;
